import { __decorate } from "tslib";
import { Component, Prop, Mixins, Watch } from 'vue-property-decorator';
import { requiredProperty, invalidType } from '@/helpers/mixins/LocalizationHelper';
import { normalizeKey } from '@/helpers';
import moment from 'moment';
import { ECOMMERCE_B2C, SHOPIFY, INSURANCE } from '@/helpers/constants/Bundles';
import BaseDatePicker from '@/components/datepicker/Index.vue';
import CreateXenditCustomer from '@/plugins/graphql/mutations/create-xendit-customer.gql';
import AppDetailsMixin from '@/helpers/vue_mixins/v2/ecommerce/AppDetailsMixin';
import { AlertType } from '@/helpers/entities/enums/AlertType';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import NetworkDistributorMixin from '@/helpers/vue_mixins/v2/network_distributor/NetworkDistributorMixin';
import AppBasic from '@/plugins/graphql/queries/v2/app-basic.gql';
import FormHeader from '@/components/v2/FormHeader.vue';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import ActionButton from '@/components/action_button/Index.vue';
import PhoneInput from '@/components/v2/PhoneInput.vue';
import { required, email } from 'vuelidate/lib/validators';
import Modal from '@/components/v3/Modal.vue';
import { Mutation } from 'vuex-class';
import { NAV_DRAWER, SHORTDATE_FORMAT } from '@/helpers/constants/app';
import { STORE_MANAGEMENT, DISTRIBUTOR, AFFILIATE } from '@/helpers/constants/CustomBundleKeys';
import { TERMS_AND_AGREEMENT_ROUTE } from '@/helpers/constants/GlobalRoutes';
const opts = { namespace: NAV_DRAWER };
/**
 * SideNav Component
 * Reactively toggle Navigation Drawer via Vuex Store state `toggleDrawer`.
 * Which is received as `Prop` from Parent Component.
 * Render SideNav Menu Items when Prop `appUUID` is present.
 * Reassign `toggle` Prop into `toggleDrawer Property` since Props should
 * not be directly mutated.
 */
// TODO: fetch if accessToken exists, use state if dialog will be shown
let SideNav = class SideNav extends Mixins(AppDetailsMixin, ViewportSizeMixin, NetworkDistributorMixin, Mixins(FormMixin, FormValidationsMixin)) {
    constructor() {
        super(...arguments);
        this.previousPath = null;
        this.billingPathParams = {
            name: '',
            params: {
                appUUID: '',
            },
        };
        this.genders = [
            {
                gender: 'MALE',
                uuid: '1',
            },
            {
                gender: 'FEMALE',
                uuid: '2',
            },
        ];
        this.form = {
            firstName: '',
            lastName: '',
            email: '',
            mobile: '',
            nationality: 'PH',
        };
        this.existingUser = false;
        this.displayBillingMenu = true;
        this.enableAffiliate = false;
        this.drawer = this.toggle;
        this.fromSideNav = false;
    }
    onRouteChanged(to, from) {
        this.previousPath = from;
        this.navigateTo(to, false);
    }
    isValidMobile(mobileNumber) {
        // Remove all spaces from the input
        if (mobileNumber) {
            const cleanedNumber = mobileNumber.replace(/\s/g, '');
            // Test against the regex for the cleaned number
            const phoneNumberRegex = /^\+639\d{9}$/;
            return phoneNumberRegex.test(cleanedNumber);
        }
    }
    closeModal() {
        const vm = this;
        this.$emit('closeModal');
        if (vm.previousPath &&
            !['payment-preferences', 'billing', 'billing-details'].includes(vm.previousPath.name)) {
            vm.navigateBack(vm.previousPath);
        }
        else {
            let name = 'overview';
            if (ECOMMERCE_B2C) {
                name = 'ecommerce-overview';
                vm.$router.push({ name });
            }
            else if (SHOPIFY) {
                name = 'shopify-overview';
                vm.$router.push({ name });
            }
        }
    }
    get today() {
        return moment().format(SHORTDATE_FORMAT);
    }
    async createCustomer() {
        const noSpaceMobile = this.form.mobile.replace(/\s/g, '');
        return this.$apollo.mutate({
            mutation: CreateXenditCustomer,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    givenNames: this.form.firstName,
                    surname: this.form.lastName,
                    email: this.form.email,
                    mobileNumber: noSpaceMobile,
                    nationality: this.form.nationality,
                },
            },
        });
    }
    formCallback() {
        const message = 'Successfully Created Xendit User';
        this.toggleAlert(message, AlertType.success);
    }
    async submit() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            this.isRequestOnProcess = true;
            const mutation = this.createCustomer();
            await mutation
                .then(() => {
                this.formCallback();
                this.$emit('xenditCustomerCreateSuccess');
                this.$router.push({
                    name: this.billingPathParams.name,
                    params: this.billingPathParams.params,
                });
                this.$emit('closeModal');
            })
                .catch((error) => {
                this.errorNotification(error.message);
            })
                .finally(() => {
                this.isRequestOnProcess = false;
            });
        }
    }
    hasUrl(name) {
        const urlRegex = new RegExp('([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?');
        return !urlRegex.test(name);
    }
    nationalityChecker(nationality) {
        return /^[A-Z]{2}$/.test(nationality);
    }
    get trailingWhitespace() {
        return (value) => {
            const hasTrailingWhitespace = value.trim() !== value;
            return hasTrailingWhitespace;
        };
    }
    getEmailErrors(emailList, emailType, emailTypeSpace) {
        const errors = [];
        let hasWhitespace = false; // Flag to track if any email has whitespace
        if (emailList && this.trailingWhitespace(emailList)) {
            hasWhitespace = true;
            errors.push('Invalid input. Please make sure that there are no whitespaces before or after the email addresses provided');
        }
        // If no whitespaces were found and there are other errors, push the invalidType error message
        if (!hasWhitespace && this.$v.form[emailType].$anyError) {
            errors.push(invalidType(emailTypeSpace));
        }
        return errors;
    }
    get firstNameErrors() {
        const errors = [];
        if (!this.$v.form.firstName.$dirty) {
            return errors;
        }
        if (!this.$v.form.firstName.required) {
            const msg = requiredProperty('First Name');
            errors.push(msg);
        }
        if (!this.$v.form.firstName.invalidFirstName) {
            const msg = 'First Name Contains Invalid Characters';
            errors.push(msg);
        }
        return errors;
    }
    get lastNameErrors() {
        const errors = [];
        if (!this.$v.form.lastName.$dirty) {
            return errors;
        }
        if (!this.$v.form.lastName.required) {
            const msg = requiredProperty('Last Name');
            errors.push(msg);
        }
        if (!this.$v.form.lastName.invalidLastName) {
            const msg = 'Last Name Contains Invalid Characters';
            errors.push(msg);
        }
        return errors;
    }
    get phoneErrors() {
        const errors = [];
        if (!this.$v.form.mobile.$dirty) {
            return errors;
        }
        if (!this.$v.form.mobile.required) {
            const msg = requiredProperty('Mobile Number');
            errors.push(msg);
        }
        if (!this.$v.form.mobile.invalidMobile) {
            const msg = 'Mobile Number is invalid';
            errors.push(msg);
        }
        return errors;
    }
    get nationalityErrors() {
        const errors = [];
        if (!this.$v.form.nationality.$dirty) {
            return errors;
        }
        if (!this.$v.form.nationality.required) {
            const msg = requiredProperty('Nationality');
            errors.push(msg);
        }
        if (!this.$v.form.nationality.invalidNationality) {
            const msg = 'Nationality format is incorrect. For Philippines use PH';
            errors.push(msg);
        }
        return errors;
    }
    get settingsIconPath() {
        return require('@/assets/images/setting.svg');
    }
    get newSettingsIconPath() {
        return require('@/assets/images/settings-orange.svg');
    }
    get settingsIcon() {
        if (this.isB2c) {
            return this.settingsIconPath;
        }
        return this.newSettingsIconPath;
    }
    get setupStoreIcon() {
        return require('@/assets/images/store-setup-orange.svg');
    }
    get termsAndAgreeementIcon() {
        if (this.isB2c) {
            return require('@/assets/images/terms-cyan.svg');
        }
        return require('@/assets/images/terms-orange.svg');
    }
    get affiliateSettingsIcon() {
        return require('@/assets/images/affiliate-settings.svg');
    }
    get faqIcon() {
        return require('@/assets/images/faq.svg');
    }
    buildBillingsSubMenu() {
        const params = { appUUID: this.appUUID };
        const commonChildrenParams = { icon: '', exact: true };
        return [
            {
                ...commonChildrenParams,
                text: 'Billing Manager',
                path: { name: 'billing', params },
                exact: true,
            },
            {
                ...commonChildrenParams,
                text: 'Payment Preferences Manager',
                path: { name: 'payment-preferences', params },
            },
        ];
    }
    get termsAndAgreementMenu() {
        return {
            icon_path: this.termsAndAgreeementIcon,
            text: TERMS_AND_AGREEMENT_ROUTE.title,
            path: { name: TERMS_AND_AGREEMENT_ROUTE.name, params: { appUUID: this.appUUID } },
            exact: true,
        };
    }
    get networkDistributorMenus() {
        return [
            {
                icon_path: require('@/components/v2/side_nav/assets/overview-orange.svg'),
                exact: true,
                text: 'Overview',
                path: { name: 'reseller-mini-apps-overview', params: { resellerUuid: this.resellerUuid } },
            },
            {
                icon_path: require('@/components/v2/side_nav/assets/store.svg'),
                exact: true,
                text: 'Merchants',
                path: {
                    name: 'reseller-merchants-by-status',
                    params: { status: 'activated' },
                },
            },
        ];
    }
    automationRouteSelect() {
        if (this.bundleKey === 'affiliate-bundle') {
            return { name: 'affiliate-automation' };
        }
        return { name: 'ecommerce-affiliate-automation' };
    }
    get automationMenu() {
        const route = this.automationRouteSelect();
        return {
            icon_path: this.settingsIcon,
            text: 'Comment Auto-Reply',
            path: { name: route.name, params: { appUUID: this.appUUID } },
            exact: true,
        };
    }
    get faqMenu() {
        return {
            icon_path: this.faqIcon,
            text: 'FAQ Manager',
            path: { name: 'faq-manager', params: { appUUID: this.appUUID } },
            exact: true,
        };
    }
    get globalMenus() {
        const defaultMenu = [this.storeSetup, this.settingsMenu()];
        if (this.bundleKey === STORE_MANAGEMENT || this.isDistributor || this.isAffiliate) {
            return defaultMenu;
        }
        if (this.displayBillingMenu) {
            const additionalMenu = [this.faqMenu, ...defaultMenu];
            return additionalMenu;
        }
        const b2cKey = normalizeKey(ECOMMERCE_B2C).toLowerCase();
        const shopifyKey = normalizeKey(SHOPIFY).toLowerCase();
        const key = this.bundle.toLowerCase();
        const menu = [...defaultMenu];
        if (key === b2cKey || key === shopifyKey) {
            menu.unshift(this.faqMenu);
        }
        return menu;
    }
    get storeSetup() {
        const b2cKey = normalizeKey(ECOMMERCE_B2C).toLowerCase();
        const shopifyKey = normalizeKey(SHOPIFY).toLowerCase();
        // const distributor = normalizeKey(DISTRIBUTOR).toLowerCase();
        const insurance = normalizeKey(INSURANCE).toLowerCase();
        const key = this.bundle.toLowerCase();
        if (key === b2cKey) {
            return this.storeSetupMenu(this.ecommerceSubMenu);
        }
        if (key === shopifyKey) {
            return this.storeSetupMenu(this.shopifySubMenu);
        }
        if (key === insurance) {
            return this.storeSetupMenu(this.insuranceSubMenu);
        }
        return this.storeSetupMenu;
    }
    get storeSetupIsActive() {
        return [/store-setup/, /notifications\/email/, /terms-and-agreement/].some((regex) => regex.test(this.$route.fullPath));
    }
    storeSetupMenu(subMenus) {
        return {
            icon_path: this.setupStoreIcon,
            text: 'Store Setup',
            model: this.storeSetupIsActive,
            children: subMenus,
        };
    }
    // Eccomerce Sub Menu
    get ecommerceSubMenu() {
        const params = { appUUID: this.appUUID };
        const commonChildrenParams = { icon: '', exact: true };
        const menu = [
            {
                ...commonChildrenParams,
                text: 'Checkout Settings',
                path: {
                    name: 'ecommerce-cart-settings',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Delivery Areas',
                path: {
                    name: 'ecommerce-delivery-areas',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Shipping Manager',
                path: {
                    name: 'ecommerce-shipping',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Payment Manager',
                path: {
                    name: 'ecommerce-payment',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: TERMS_AND_AGREEMENT_ROUTE.title,
                path: { name: TERMS_AND_AGREEMENT_ROUTE.name, params },
                exact: false,
            },
        ];
        menu.push({
            ...commonChildrenParams,
            text: 'Notification Manager',
            path: {
                name: 'ecommerce-notifications',
                params,
            },
            exact: false,
        });
        return menu;
    }
    // Shopify Sub Menu
    get shopifySubMenu() {
        const params = { appUUID: this.appUUID };
        const commonChildrenParams = { icon: '', exact: true };
        const menu = [
            {
                ...commonChildrenParams,
                text: 'Checkout Settings',
                path: {
                    name: 'shopify-cart-settings',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Delivery Areas',
                path: {
                    name: 'shopify-delivery-areas',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Shipping Manager',
                path: {
                    name: 'shopify-shipping',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Payment Manager',
                path: {
                    name: 'shopify-payment',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: TERMS_AND_AGREEMENT_ROUTE.title,
                path: { name: TERMS_AND_AGREEMENT_ROUTE.name, params },
                exact: false,
            },
        ];
        menu.push({
            ...commonChildrenParams,
            text: 'Notification Manager',
            path: {
                name: 'shopify-notifications',
                params,
            },
            exact: false,
        });
        return menu;
    }
    // Insurance Sub Menu
    get insuranceSubMenu() {
        const params = { appUUID: this.appUUID };
        const commonChildrenParams = { icon: '' };
        const menu = [
            {
                ...commonChildrenParams,
                text: 'Checkout Settings',
                path: {
                    name: 'insurance-cart-settings',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Delivery Areas',
                path: {
                    name: 'insurance-delivery-areas',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Shipping Manager',
                path: {
                    name: 'insurance-shipping',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: 'Payment Manager',
                path: {
                    name: 'insurance-payment',
                    params,
                },
                exact: false,
            },
            {
                ...commonChildrenParams,
                text: TERMS_AND_AGREEMENT_ROUTE.title,
                path: { name: TERMS_AND_AGREEMENT_ROUTE.name, params },
                exact: false,
            },
        ];
        menu.push({
            ...commonChildrenParams,
            text: 'Notification Manager',
            path: {
                name: 'ecommerce-notifications',
                params,
            },
            exact: false,
        });
        return menu;
    }
    get bundleSideNavTemplate() {
        // Dynamically Import Component
        // https://twitter.com/DamianDulisz/status/903569400095285248/photo/1\
        return () => import(`@/components/v2/side_nav/${this.bundle}.vue`);
    }
    get currentRoute() {
        return this.$router.currentRoute;
    }
    get bundleKey() {
        const { key } = this.currentRoute.params;
        return key;
    }
    get bundle() {
        return normalizeKey(this.bundleKey || '');
    }
    get isB2c() {
        const b2cKey = normalizeKey(ECOMMERCE_B2C).toLowerCase();
        const shopifyKey = normalizeKey(SHOPIFY).toLowerCase();
        const insuranceKey = normalizeKey(INSURANCE).toLowerCase();
        const key = this.bundle.toLowerCase();
        return key === b2cKey || key === shopifyKey || key === insuranceKey;
    }
    get isStoreManagement() {
        const key = this.bundle.toLowerCase();
        return key === STORE_MANAGEMENT;
    }
    get drawerWidth() {
        if (this.xsOnly) {
            return '230';
        }
        return '332';
    }
    get settingsActive() {
        if (/affiliate-settings/.test(this.$route.fullPath) ||
            /store-setup/.test(this.$route.fullPath)) {
            return false;
        }
        return [/settings/, /billing/].some((regex) => regex.test(this.$route.fullPath));
    }
    /*
     * Fix issue: v-navigation-drawer is not always full height
     * Reference: https://github.com/vuetifyjs/vuetify/issues/4384#issuecomment-620617356
     */
    get drawerHeight() {
        if (this.xsOnly) {
            return 'calc(100% - 62px)';
        }
        return 'calc(100% - 72px)';
    }
    buildSettingsSubMenu() {
        const params = { appUUID: this.appUUID };
        const commonChildrenParams = { icon: '', exact: true };
        const defaultLinks = [
            {
                ...commonChildrenParams,
                text: 'Manage Access',
                path: { name: 'members', params },
            },
            {
                ...commonChildrenParams,
                text: 'Maintenance',
                path: { name: 'app-maintenance', params },
            },
        ];
        const distributorLinks = [
            {
                ...commonChildrenParams,
                text: 'Members',
                path: { name: 'members', params },
            },
        ];
        if (this.isStoreManagement) {
            return defaultLinks;
        }
        if (this.isDistributor) {
            return distributorLinks;
        }
        if (this.isAffiliate) {
            return [
                defaultLinks[0],
                {
                    ...commonChildrenParams,
                    text: 'Connect / Disconnect ChatGenie',
                    icon: '',
                    exact: false,
                    path: { name: 'affiliate-page-subscription' },
                },
            ];
        }
        return [
            defaultLinks[0],
            ...(this.displayBillingMenu ? this.buildBillingsSubMenu() : []),
            {
                ...commonChildrenParams,
                text: 'Connect / Disconnect ChatGenie',
                icon: '',
                exact: false,
                path: { name: 'page-subscription' },
            },
            {
                ...commonChildrenParams,
                text: 'In-App Store Settings',
                path: { name: 'mini-app-settings', params },
            },
            ...(this.$store.state.isInboxManagerAllowedApp
                ? [{
                        ...commonChildrenParams,
                        text: 'Chatbot Settings',
                        path: { name: 'ai-settings' },
                    }]
                : []),
            defaultLinks[1],
        ];
    }
    settingsMenu() {
        const text = this.isB2c ? 'System and Account Settings' : 'Settings';
        return {
            icon_path: this.settingsIcon,
            text,
            model: this.settingsActive,
            children: this.buildSettingsSubMenu(),
        };
    }
    onToggle(val) {
        if (!val) {
            this.toggleDrawer(false);
        }
    }
    get isDistributor() {
        return this.bundleKey === DISTRIBUTOR;
    }
    get isAffiliate() {
        return this.bundleKey === AFFILIATE;
    }
    get sideNaveMenus() {
        if (this.resellerUuid) {
            return this.networkDistributorMenus;
        }
        return this.globalMenus;
    }
    navigateTo(path, fromSideNav) {
        if (fromSideNav) {
            this.fromSideNav = true;
        }
        else {
            this.fromSideNav = false;
        }
        if (path.name === 'payment-preferences' ||
            path.name === 'billing' ||
            path.name === 'billing-details') {
            if (this.checkForCustomerXenditId()) {
                this.$router.push({ name: path.name, params: path.params });
            }
            else {
                this.billingPathParams.name = path.name;
                this.billingPathParams.params = path.params;
                this.$emit('openModal');
            }
        }
        else {
            this.$router.push({ name: path.name, params: path.params });
        }
    }
    navigateBack(path) {
        this.$router.push({ name: path.name, params: path.params });
    }
    checkForCustomerXenditId() {
        return this.existingXenditCustomer;
    }
    get existingCustomer() {
        const vm = this;
        const isBillingRoute = Boolean(vm.$route.matched.find((route) => route.path === '/apps/:key/:appUUID/billing'));
        return ((!vm.existingXenditCustomer && isBillingRoute) ||
            (!vm.existingXenditCustomer && vm.fromSideNav));
    }
};
__decorate([
    Mutation('untoggleNestedMenus', { namespace: NAV_DRAWER })
], SideNav.prototype, "untoggleNestedMenus", void 0);
__decorate([
    Mutation('toggleDrawer', opts)
], SideNav.prototype, "toggleDrawer", void 0);
__decorate([
    Mutation('showInbox', { namespace: NAV_DRAWER })
], SideNav.prototype, "showInbox", void 0);
__decorate([
    Prop()
], SideNav.prototype, "toggle", void 0);
__decorate([
    Prop()
], SideNav.prototype, "appUUID", void 0);
__decorate([
    Prop()
], SideNav.prototype, "resellerUuid", void 0);
__decorate([
    Prop()
], SideNav.prototype, "existingXenditCustomer", void 0);
__decorate([
    Watch('$route')
], SideNav.prototype, "onRouteChanged", null);
SideNav = __decorate([
    Component({
        apollo: {
            appBasic: {
                query: AppBasic,
                variables() {
                    return {
                        appUuid: this.appUUID,
                    };
                },
                update(data) {
                    this.form.firstName = data.app.user.firstName;
                    this.form.lastName = data.app.user.lastName;
                    this.form.email = data.app.user.businessEmailAddress;
                    this.form.mobile = ` +63${data.app.user.contactNumber}`;
                    this.displayBillingMenu = data.app.displayBillingMenu;
                    this.enableAffiliate = data.app.enableAffiliate;
                },
            },
        },
        components: {
            HelpCenterWidget: () => import('./HelpCenterWidget.vue'),
            BaseTextField,
            ActionButton,
            FormHeader,
            Modal,
            BaseDatePicker,
            PhoneInput,
        },
        validations: {
            form: {
                firstName: {
                    required,
                    invalidFirstName() {
                        const vm = this;
                        const { firstName } = this.form;
                        return vm.hasUrl(firstName);
                    },
                },
                lastName: {
                    required,
                    invalidLastName() {
                        const vm = this;
                        const { lastName } = this.form;
                        return vm.hasUrl(lastName);
                    },
                },
                email: {
                    required,
                    email,
                },
                nationality: {
                    required,
                    invalidNationality() {
                        const vm = this;
                        const { nationality } = this.form;
                        return vm.nationalityChecker(nationality);
                    },
                },
                mobile: {
                    required,
                    invalidMobile() {
                        const vm = this;
                        const { mobile } = this.form;
                        return vm.isValidMobile(mobile);
                    },
                },
            },
        },
    })
], SideNav);
export default SideNav;
